import React, { useEffect, useRef, useState } from 'react';
import { LuPencil, LuEllipsisVertical, LuSearch, LuTrash2 } from 'react-icons/lu';
import StarRatingDisplay from './StarRatingDisplay';
import './ReviewsTable.css';

const TABS = [
  { key: 'all', label: 'Todas' },
  { key: 'active', label: 'Ativas' },
  { key: 'inactive', label: 'Inativas' },
];

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

// Lista de páginas truncada (ex: 1 2 3 … 8 9 10) — sem isso, com "Todos os
// produtos" selecionado (bem mais avaliações, logo bem mais páginas), a
// paginação tentava desenhar um botão por página numa linha só e estourava
// o rodapé do card.
export const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const delta = 1;
  const range = [];
  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  const withDots = [];
  let last = null;
  for (const page of range) {
    if (last !== null) {
      if (page - last === 2) withDots.push(last + 1);
      else if (page - last > 2) withDots.push('…');
    }
    withDots.push(page);
    last = page;
  }
  return withDots;
};

const RowActions = ({ review, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="reviews-table__actions">
      <button
        type="button"
        className="reviews-table__icon-btn"
        aria-label="Editar avaliação"
        onClick={() => onEdit(review)}
      >
        <LuPencil size={16} />
      </button>
      <div className="reviews-table__kebab" ref={menuRef}>
        <button
          type="button"
          className="reviews-table__icon-btn"
          aria-label="Mais ações"
          onClick={() => setOpen((o) => !o)}
        >
          <LuEllipsisVertical size={16} />
        </button>
        {open && (
          <div className="reviews-table__kebab-menu">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onEdit(review);
              }}
            >
              <LuPencil size={14} /> Editar
            </button>
            <button
              type="button"
              className="reviews-table__kebab-menu-item--danger"
              onClick={() => {
                setOpen(false);
                onDelete(review);
              }}
            >
              <LuTrash2 size={14} /> Excluir
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewsTable = ({
  reviews,
  counts,
  tab,
  onTabChange,
  search,
  onSearchChange,
  onEdit,
  onDelete,
  page,
  totalPages,
  onPageChange,
}) => {
  return (
    <div className="cp-card reviews-table-card">
      <div className="reviews-table-toolbar">
        <div className="reviews-table-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`reviews-table-tab${tab === t.key ? ' reviews-table-tab--active' : ''}`}
              onClick={() => onTabChange(t.key)}
            >
              {t.label} <span className="reviews-table-tab__count">{counts[t.key]}</span>
            </button>
          ))}
        </div>
        <div className="reviews-table-search">
          <LuSearch size={16} />
          <input
            type="text"
            placeholder="Buscar avaliação..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      {reviews.length > 0 ? (
        <div className="reviews-table-scroll">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Avaliação</th>
                <th>Mensagem</th>
                <th>Data</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review._id}>
                  <td>
                    <div className="reviews-table__customer">
                      <span className="reviews-table__avatar">{getInitials(review.name)}</span>
                      <span>{review.name}</span>
                    </div>
                  </td>
                  <td>
                    <StarRatingDisplay rating={review.rating} size={14} />
                  </td>
                  <td className="reviews-table__message">{review.message}</td>
                  <td className="reviews-table__date">{formatDate(review.createdAt)}</td>
                  <td>
                    <span className={`reviews-table__badge${review.isDeleted ? '' : ' reviews-table__badge--active'}`}>
                      {review.isDeleted ? 'Inativa' : 'Ativa'}
                    </span>
                  </td>
                  <td>
                    <RowActions review={review} onEdit={onEdit} onDelete={onDelete} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="reviews-table__empty">Nenhuma avaliação encontrada.</p>
      )}

      {totalPages > 1 && (
        <div className="reviews-table-pagination">
          <button
            type="button"
            className="cp-btn cp-btn--secondary"
            disabled={page === 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            ‹
          </button>
          {buildPageList(page, totalPages).map((p, i) =>
            p === '…' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`ellipsis-${i}`} className="reviews-table-pagination__ellipsis">
                {p}
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`reviews-table-pagination__page${p === page ? ' reviews-table-pagination__page--active' : ''}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="cp-btn cp-btn--secondary"
            disabled={page === totalPages}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewsTable;
